import React, { useState } from 'react';
import {
	Box,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	TablePagination,
} from '@mui/material';
import { Theme } from '@mui/material/styles';

import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';

import EmptyState from 'common/components/states/EmptyState';
import { useEffect } from 'react';

import {
	InvoiceWithStoreDetails,
	InvoiceTableStrings,
	ExtraColumn,
	DEFAULT_STRINGS,
} from './InvoiceTable.types';

import LoadingState from 'common/components/states/LoadingState';

import InvoiceTableRow from './InvoiceTableRow';

interface Props {
	data: InvoiceWithStoreDetails[];
	showAdminActions?: boolean;
	error?: boolean;
	loading?: boolean;
	strings?: InvoiceTableStrings;
	onError?: (err: string) => void;
	extraColumns?: ExtraColumn[];
}

const InvoiceTable = (props: Props) => {
	const classes = useStyles();
	const { strings = DEFAULT_STRINGS } = props;

	const [rowsPerPage, setRowsPerPage] = useState<number>(10);
	const [page, setPage] = useState<number>(0);
	const totalPages = props.data.length / rowsPerPage;
	const columnCount = 7 + (props.extraColumns?.length ?? 0);

	useEffect(() => {
		if (page >= totalPages) {
			setPage(Math.max(totalPages - 1, 0));
		}
	}, [totalPages, page]);

	const getCurrentPageData = () => {
		return props.data.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
	};

	const renderTableBody = () => {
		if (props.loading) {
			return (
				<TableRow>
					<TableCell colSpan={columnCount}>
						<LoadingState />
					</TableCell>
				</TableRow>
			);
		}

		if (props.error) {
			return (
				<TableRow>
					<TableCell colSpan={columnCount}>
						<EmptyState
							title={strings.errorStateTitle}
							description={strings.errorStateDescription}
						/>
					</TableCell>
				</TableRow>
			);
		}

		if (props.data.length === 0) {
			return (
				<TableRow>
					<TableCell colSpan={columnCount}>
						<EmptyState
							title={strings.emptyStateTitle}
							description={strings.emptyStateDescription}
						/>
					</TableCell>
				</TableRow>
			);
		}

		return getCurrentPageData().map((invoice) => (
			<InvoiceTableRow
				key={invoice.id}
				invoice={invoice}
				strings={strings}
				showAdminActions={props.showAdminActions}
				onError={props.onError}
				extraColumns={props.extraColumns}
			/>
		));
	};

	return (
		<Box className={classes.wrapper}>
			<Table className={classes.table}>
				<TableHead className={classes.tableHead}>
					<TableRow>
						<TableCell align="left" className={classes.tableHeadCell}>
							{strings.headerBillingCycle}
						</TableCell>
						<TableCell align="left" className={classes.tableHeadCell}>
							{strings.headerDateIssued}
						</TableCell>
						{props.extraColumns?.includes('merchantName') && (
							<TableCell align="left" className={classes.tableHeadCell}>
								{/**
								 * Internal admin only, no need to localize
								 */}
								Merchant
							</TableCell>
						)}
						<TableCell align="left" className={classes.tableHeadCell}>
							{strings.headerStore}
						</TableCell>
						<TableCell align="left" className={classes.tableHeadCell}>
							{strings.headerInvoiceNo}
						</TableCell>
						<TableCell align="left" className={classes.tableHeadCell}>
							{strings.headerStatus}
						</TableCell>
						<TableCell align="right" className={classes.tableHeadCell}>
							{strings.headerAmount}
						</TableCell>
						<TableCell className={classes.tableHeadCell}></TableCell>
					</TableRow>
				</TableHead>
				<TableBody>{renderTableBody()}</TableBody>
			</Table>
			<Box width="100%" minWidth={700} display="flex" flexDirection="row" justifyContent="flex-end">
				<TablePagination
					rowsPerPage={rowsPerPage}
					count={props.data.length}
					rowsPerPageOptions={[10, 50, 100]}
					component="div"
					page={page}
					onPageChange={(e, page) => setPage(page)}
					onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
				/>
			</Box>
		</Box>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		wrapper: {
			position: 'relative',
			overflowX: 'scroll',
		},
		table: {
			borderCollapse: 'collapse',
			minWidth: 900,
		},
		tableHead: {
			background: theme.palette.background.secondary,
		},
		tableHeadCell: {
			fontWeight: 600,
			fontSize: 14,
			padding: theme.spacing(2.5),
		},
		tableRow: {
			borderBottom: `1px solid ${theme.palette.border.table}`,
		},
	}),
);

export default InvoiceTable;
